import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export interface ControlInventoryAsset {
  id: number;
  tenant_id: number;
  name: string;
  type: string;
  sku?: string | null;
  entry_date?: string | null;
  seal_item?: string | null;
  category?: string | null;
  brand?: string | null;
  subcategory?: string | null;
  details?: string | null;
  description?: string | null;
  photo_url?: string | null;
  has_photo?: number | boolean;
  location_name?: string | null;
  address?: string | null;
  damages?: string | null;
  kit_id?: number | null;
  kit_name?: string | null;
  kit_code?: string | null;
  department_id?: number | null;
  department_name?: string | null;
  sector_id?: number | null;
  sector_name?: string | null;
  asset_nature?: 'PHYSICAL' | 'VIRTUAL' | 'CLOUD_SERVICE' | null;
  hosting_provider?: string | null;
  environment_name?: string | null;
  external_identifier?: string | null;
  cloud_account?: string | null;
  cloud_region?: string | null;
  hostname_ip?: string | null;
  has_reservation?: number | boolean | null;
  reservation_end_date?: string | null;
  status: 'ACTIVE' | 'STOCK' | 'BROKEN';
  created_by: number;
  created_by_name?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
  linked_users_count?: number;
  linked_user_names?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface ControlInventoryKit {
  id: number;
  tenant_id: number;
  name: string;
  code?: string | null;
  description?: string | null;
  status: 'ACTIVE' | 'INACTIVE';
  created_by: number;
  created_at: string;
  updated_at?: string | null;
  assets_count?: number;
}

export interface ControlInventoryCatalogItem {
  id: number;
  tenant_id: number;
  catalog_type: 'TYPE' | 'CATEGORY' | 'SUBCATEGORY' | 'BRAND' | 'LOCATION';
  parent_id?: number | null;
  parent_name?: string | null;
  parent_catalog_type?: string | null;
  name: string;
  status: 'ACTIVE' | 'INACTIVE';
  created_by: number;
  created_by_name?: string | null;
  updated_by?: number | null;
  updated_by_name?: string | null;
  created_at: string;
  updated_at?: string | null;
}

export interface ControlInventoryHistoryItem {
  id: number;
  tenant_id: number;
  entity_type: 'ASSET' | 'KIT';
  entity_id: number;
  action_type: string;
  details?: string | null;
  created_by: number;
  created_by_name?: string | null;
  created_at: string;
  asset_name?: string | null;
  kit_name?: string | null;
}

export interface ControlInventoryAssetUser {
  id: number;
  tenant_id: number;
  asset_id: number;
  user_id: number;
  created_at: string;
  user_name?: string | null;
  user_email?: string | null;
}

export interface ControlInventoryDashboard {
  active_assets: number;
  inactive_assets: number;
  kits: number;
  damaged_assets: number;
  assets_with_kit?: number;
  assets_without_kit?: number;
  by_type: Array<{ type: string; total: number }>;
  by_status?: Array<{ status: string; total: number }>;
  by_category?: Array<{ label: string; total: number }>;
  by_brand?: Array<{ label: string; total: number }>;
  by_location?: Array<{ label: string; total: number }>;
  by_department?: Array<{ department_id: number; label: string; total: number }>;
  by_kit?: Array<{ kit_id: number; label: string; total: number }>;
  by_created_by?: Array<{ user_id: number; label: string; total: number }>;
  by_updated_by?: Array<{ user_id: number; label: string; total: number }>;
  by_created_month?: Array<{ month_key: string; total: number }>;
  recent_history: ControlInventoryHistoryItem[];
}

export interface ControlInventoryAssetListResponse {
  rows: ControlInventoryAsset[];
  total: number;
  limit: number;
  offset: number;
}

@Injectable({ providedIn: 'root' })
export class ControlinventoryService {
  private readonly baseUrl = `${API_BASE_URL}/api/controlinventory`;

  constructor(private http: HttpClient) {}

  getDashboard(tenantId: number, filters: Record<string, any> = {}): Observable<ControlInventoryDashboard> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== 'ALL') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlInventoryDashboard>(`${this.baseUrl}/dashboard`, { params });
  }

  getAssets(tenantId: number, filters: Record<string, any> = {}): Observable<ControlInventoryAssetListResponse> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlInventoryAssetListResponse>(`${this.baseUrl}/assets`, { params });
  }

  getAssetPhotoUrl(tenantId: number, asset: Pick<ControlInventoryAsset, 'id' | 'updated_at' | 'created_at'>): string {
    const version = encodeURIComponent(String(asset.updated_at || asset.created_at || ''));
    return `${this.baseUrl}/assets/${asset.id}/photo?tenant_id=${tenantId}&v=${version}`;
  }

  createAsset(payload: any): Observable<ControlInventoryAsset> {
    return this.http.post<ControlInventoryAsset>(`${this.baseUrl}/assets`, payload);
  }

  updateAsset(assetId: number, payload: any): Observable<ControlInventoryAsset> {
    return this.http.put<ControlInventoryAsset>(`${this.baseUrl}/assets/${assetId}`, payload);
  }

  getAssetUsers(tenantId: number, assetId: number): Observable<ControlInventoryAssetUser[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlInventoryAssetUser[]>(`${this.baseUrl}/assets/${assetId}/users`, { params });
  }

  updateAssetUsers(assetId: number, payload: { tenant_id: number; updated_by?: number | null; user_ids: Array<number | string> }): Observable<ControlInventoryAssetUser[]> {
    return this.http.put<ControlInventoryAssetUser[]>(`${this.baseUrl}/assets/${assetId}/users`, payload);
  }

  deactivateAsset(tenantId: number, assetId: number, updatedBy?: number | null) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/assets/${assetId}`, {
      params,
      body: { updated_by: updatedBy || null }
    });
  }

  getKits(tenantId: number): Observable<ControlInventoryKit[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlInventoryKit[]>(`${this.baseUrl}/kits`, { params });
  }

  createKit(payload: any): Observable<ControlInventoryKit> {
    return this.http.post<ControlInventoryKit>(`${this.baseUrl}/kits`, payload);
  }

  updateKit(kitId: number, payload: any): Observable<ControlInventoryKit> {
    return this.http.put<ControlInventoryKit>(`${this.baseUrl}/kits/${kitId}`, payload);
  }

  deactivateKit(tenantId: number, kitId: number, updatedBy?: number | null) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/kits/${kitId}`, {
      params,
      body: { updated_by: updatedBy || null }
    });
  }

  getHistory(tenantId: number, filters: Record<string, any> = {}): Observable<ControlInventoryHistoryItem[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });
    return this.http.get<ControlInventoryHistoryItem[]>(`${this.baseUrl}/history`, { params });
  }

  getCatalogs(tenantId: number, catalogType?: string | null): Observable<ControlInventoryCatalogItem[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (catalogType) {
      params = params.set('catalog_type', catalogType);
    }
    return this.http.get<ControlInventoryCatalogItem[]>(`${this.baseUrl}/catalogs`, { params });
  }

  createCatalog(payload: any): Observable<ControlInventoryCatalogItem> {
    return this.http.post<ControlInventoryCatalogItem>(`${this.baseUrl}/catalogs`, payload);
  }

  updateCatalog(catalogId: number, payload: any): Observable<ControlInventoryCatalogItem> {
    return this.http.put<ControlInventoryCatalogItem>(`${this.baseUrl}/catalogs/${catalogId}`, payload);
  }

  deactivateCatalog(tenantId: number, catalogId: number, updatedBy?: number | null) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.delete(`${this.baseUrl}/catalogs/${catalogId}`, {
      params,
      body: { updated_by: updatedBy || null }
    });
  }
}
